import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Play, Send } from 'lucide-react';
import { Button, Select, Switch, TextField } from '@nyce/admin-kit/ui';
import {
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  Tabs,
  useAsync,
  useNotice,
} from '../../components/common';
import { errorMessage } from '../../lib/errors';
import { formatDateTime } from '../../lib/format';
import { useParam } from '../../pages/statistics/shared';
import {
  billing,
  download,
  useBillingWrite,
  type ManualRun,
  type FileFormat,
  type PeriodInput,
} from '../api/client';
import {
  DetailTable,
  ResultTable,
  RunStatusChip,
  SendDialog,
  formatValue,
  previousMonth,
  targetsOf,
} from '../shared';
import '../../pages/statistics/statistics.css';

type PeriodMode = 'month' | 'range';
type View = 'customers' | 'installations';

/** Run a report for an environment and period, look at it, download it, send it. The selection (and the run) is in the URL. */
export function BillingReportsPage() {
  const catalog = useAsync(() => billing.catalog(), []);
  const reports = catalog.data?.reports ?? [];
  const [reportId, setReportId] = useParam('report', '');
  const report = reports.find((r) => r.id === reportId) ?? reports[0];
  const targets = targetsOf(report);
  const [targetId, setTargetId] = useParam('target', '');
  const target = targets.find((t) => t.id === targetId) ?? targets[0];
  const [mode, setMode] = useParam<PeriodMode>('period', 'month');
  const [month, setMonth] = useParam('month', previousMonth());
  const [from, setFrom] = useParam('from', '');
  const [to, setTo] = useParam('to', '');
  const [runParam, setRunParam] = useParam('run', '');
  const [view, setView] = useParam<View>('view', 'customers');
  const [keep, setKeep] = useParam<'1' | '0'>('save', '1');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [run, setRun] = useState<ManualRun | null>(null);
  const [sending, setSending] = useState(false);
  const canWrite = useBillingWrite();
  const toast = useNotice();

  // A shared link (`?run=`) opens that run.
  useEffect(() => {
    const id = Number(runParam);
    if (!id || run?.id === id) return;
    billing
      .getRun(id)
      .then(setRun)
      .catch((e) => setError(errorMessage(e)));
  }, [runParam, run?.id]);

  const period: PeriodInput | null =
    mode === 'month'
      ? /^\d{4}-\d{2}$/.test(month)
        ? { month }
        : null
      : from && to && from <= to
        ? { from, to }
        : null;

  const start = async (saveIt = keep === '1') => {
    if (!report || !target || !period) return;
    setBusy(true);
    setError(null);
    try {
      const r = await billing.run(report.id, target.id, period, saveIt);
      setRun(r);
      setRunParam(r.id ? String(r.id) : '');
      if (r.status === 'failed') setError(r.error);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const save = async (format: FileFormat) => {
    if (!run) return;
    try {
      await download(run.id ?? run.result!, format);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const remove = async () => {
    if (!run?.id) return;
    try {
      await billing.deleteRun(run.id);
      toast.success(`Deleted run #${run.id}`);
      setRun(null);
      setRunParam('');
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const result = run?.status === 'succeeded' ? run.result : null;
  const unit = result?.columns.find((c) => c.key === result.unitKey);

  return (
    <Page
      title="Billing reports"
      description="Run a report for a period: the figures come from the service's own usage data. A saved run is kept, so what was sent can be downloaded or sent again; an unsaved one is only a preview."
    >
      {toast.element}
      <ErrorBox error={catalog.error} />
      <Loading loading={catalog.loading && !catalog.data} />
      {catalog.data && !reports.length && <EmptyState title="No billing reports are registered." />}
      {report && (
        <>
          <div className="sx-toolbar">
            <Select
              label="Report"
              value={report.id}
              onChange={(v) => setReportId(v)}
              options={reports.map((r) => ({ value: r.id, label: r.title }))}
            />
            <Select
              label="Environment"
              value={target?.id ?? ''}
              onChange={(v) => setTargetId(v)}
              options={targets.map((t) => ({
                value: t.id,
                label: t.configured ? t.label : `${t.label} (not configured)`,
              }))}
            />
            <Tabs
              value={mode}
              onChange={setMode}
              options={[
                { value: 'month', label: 'Month' },
                { value: 'range', label: 'Range' },
              ]}
            />
            {mode === 'month' ? (
              <TextField
                label="Month"
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
              />
            ) : (
              <>
                <TextField label="From" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
                <TextField
                  label="To (inclusive)"
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
              </>
            )}
            <Switch
              checked={keep === '1'}
              onChange={(on) => setKeep(on ? '1' : '0')}
              label="Save to history"
            />
            <Button
              icon={<Play size={16} />}
              loading={busy}
              disabled={!period || !target}
              onClick={() => void start()}
            >
              Run
            </Button>
          </div>
          <p className="nyk-ui-hint">{report.description} Days are UTC days, as the services store them.</p>
          {target && !target.configured && (
            <p className="nyk-notice nyk-notice-warn">
              The hub has no credential for {report.product} in {target.label}; a run fails until it is
              configured (see docs/billing.md).
            </p>
          )}
        </>
      )}
      <ErrorBox error={error} />
      {run && (
        <section className="nyk-card" data-testid="billing-run" data-run={run.id ?? 'unsaved'}>
          <div className="bl-head">
            <h2>
              {result?.title ?? report?.title} — {run.periodLabel}
            </h2>
            <span className="nyk-ui-hint">{result?.targetLabel ?? run.target}</span>
            <RunStatusChip run={run} />
            <span className="sx-spacer" />
            {result && unit && (
              <span>
                <span className="nyk-ui-hint">Total billed </span>
                <span className="bl-total-figure" data-testid="billing-total">
                  {formatValue(result.total[result.unitKey], unit.format)}
                </span>{' '}
                <span className="nyk-ui-hint">{unit.label}</span>
              </span>
            )}
          </div>
          <p className="sx-meta">
            {run.id ? (
              <span>
                Run <b>#{run.id}</b> by {run.createdBy}, {formatDateTime(run.createdAt)}
              </span>
            ) : (
              <span>
                <b>Not saved</b> — a preview: not in the history and not sendable. Run it again with “Save to
                history” to keep or send it.
              </span>
            )}
            {run.sentAt && (
              <span>
                Sent {formatDateTime(run.sentAt)} to {run.recipients.join(', ')}
              </span>
            )}
            {run.sendError && <span className="nyk-text-red">Not sent: {run.sendError}</span>}
            <Link to="/billing/runs">History</Link>
          </p>
          {run.status === 'failed' && <ErrorBox error={run.error} />}
          {run.status === 'failed' && canWrite && run.id && (
            <ConfirmButton
              size="sm"
              variant="danger"
              message={`Delete the failed run #${run.id}?`}
              onConfirm={() => void remove()}
            >
              Delete
            </ConfirmButton>
          )}
          {result && (
            <>
              <div className="sx-toolbar">
                <Tabs
                  value={view}
                  onChange={setView}
                  options={[
                    { value: 'customers', label: `Per ${result.customerLabel.toLowerCase()}` },
                    { value: 'installations', label: `Per ${result.detailLabel.toLowerCase()}` },
                  ]}
                />
                <span className="sx-spacer" />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Download size={14} />}
                  onClick={() => void save('pdf')}
                >
                  PDF
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Download size={14} />}
                  onClick={() => void save('xlsx')}
                >
                  Excel
                </Button>
                {canWrite && run.id && (
                  <Button size="sm" icon={<Send size={14} />} onClick={() => setSending(true)}>
                    Send…
                  </Button>
                )}
                {!run.id && (
                  <Button size="sm" loading={busy} onClick={() => void start(true)}>
                    Save
                  </Button>
                )}
                {canWrite && run.id && (
                  <ConfirmButton
                    size="sm"
                    variant="danger"
                    message={`Delete run #${run.id} (${run.periodLabel}) from the history?`}
                    onConfirm={() => void remove()}
                  >
                    Delete
                  </ConfirmButton>
                )}
              </div>
              {view === 'customers' ? <ResultTable result={result} /> : <DetailTable result={result} />}
            </>
          )}
        </section>
      )}
      {sending && run?.id && (
        <SendDialog
          run={{ ...run, id: run.id, title: result?.title ?? run.reportId }}
          onSent={(r) => {
            setRun({ ...r, result: run.result });
            toast.success(`Sent to ${r.recipients.join(', ')}`);
          }}
          onClose={() => setSending(false)}
        />
      )}
    </Page>
  );
}
